import {Component, HostListener, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ProjectService} from "../../api/project.service";
import {concat, EMPTY, Observable} from "rxjs";
import {Project, ProjectDetails, ProjectStatus, ProjectType, removePrefix, typeToPrefix} from "../../api/model/project";
import {ComponentCanDeactivate} from "../../pending-changes.guard";
import {apiErrorToMessage} from "../../common/util";
import {promptDeleteMathProblem} from "../../delete-math-problem.guard";

@Component({
  selector: 'app-project-add-edit-dialog',
  templateUrl: './project-add-edit-dialog.component.html',
  styleUrls: ['./project-add-edit-dialog.component.sass']
})
export class ProjectAddEditDialogComponent implements ComponentCanDeactivate {
  statuses: string[] = Object.values(ProjectStatus);
  verb = this.data.project?.id ? "Change" : "Create";
  nameLock: boolean = !!this.data.project?.id;
  addEditForm = new FormGroup({
    accountingId: new FormControl(removePrefix(this.prefix(), this.data.project?.accountingId), [Validators.required]),
    name: new FormControl(this.data.project?.nameWithoutParents, [Validators.required]),
    status: new FormControl(this.data.project?.status),

    deadline: new FormControl(this.data.project?.details?.deadline),
    projectCompletedDate: new FormControl(this.data.project?.details?.projectCompletedDate ? new Date(this.data.project?.details?.projectCompletedDate) : null),
    clientPreferredLanguage: new FormControl(this.data.project?.details?.clientPreferredLanguage),
    locationAddress: new FormControl(this.data.project?.details?.location.address),
    locationMapsUrl: new FormControl(this.data.project?.details?.location.mapsUrl),
    description: new FormControl(this.data.project?.details?.description?.replaceAll("<br />", "")),
    dropboxProjectLink: new FormControl(this.data.project?.details?.dropboxProjectLink),
  })
  apiError: string | undefined;

  constructor(
    private projectService: ProjectService,
    public dialogRef: MatDialogRef<ProjectAddEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProjectAddEditDialogData,
  ) {
    dialogRef.disableClose = true;
    if (this.data.project?.accountingId) {
      this.addEditForm.controls.accountingId.disable()
    }
    if (this.data.project?.id) {
      this.addEditForm.controls.name.disable()
    }
    this.addEditForm.controls.status.valueChanges.subscribe(value => {
      if (value === ProjectStatus.COMPLETE && this.addEditForm.controls.projectCompletedDate.value == null) {
        this.addEditForm.controls.projectCompletedDate.setValue(new Date(), {emitEvent: false});
      }
    });
  }

  @HostListener('window:keyup.esc') onKeyUp() {
    this.dialogRef.close();
  }

  prefix(): string | undefined {
    return this.data.type && typeToPrefix(this.data.type);
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.addEditForm.dirty;
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onPrimaryClick() {
    this.onSubmit()
  }

  onSubmit() {
    this.apiError = undefined;

    concat(
      this.updateNameAndAccountingId(),
      this.updateDetails(),
      this.updateStatus(),
    ).subscribe({
      next: (project) => {
        console.log("dialog updated project once", project);
        this.data.project = project;
      },
      error: error => {
        console.log("dialog failed to add/edit", error)
        this.apiError = apiErrorToMessage(error);
        return EMPTY
      },
      complete: () => {
        if (!this.apiError) {
          this.dialogRef.close(this.data.project || true)
        }
      }
    });

    /*.subscribe({
      next: project => {
        console.log("dialog edited project details", project)
        this.dialogRef.close(project)
      },
      error: error => {
        console.log("dialog failed editing project details", error)
        this.apiError = (error as HttpErrorResponse).message;
        return EMPTY
      }
    });*/
  }

  toggleNameLock() {
    this.nameLock = !this.nameLock
    if (this.nameLock) {
      this.addEditForm.controls.name.disable()
      this.addEditForm.controls.name.setValue(this.data.project?.nameWithoutParents)
      this.addEditForm.controls.accountingId.setValue(removePrefix(this.prefix(), this.data.project?.accountingId))
    } else {
      this.addEditForm.controls.name.enable()
    }
  }

  onDeleteClick() {
    if (promptDeleteMathProblem()) {
      this.projectService.delete(this.data.project!.id).subscribe({
        next: value => {
          console.log("project deleted", this.data.project!.id, value);
          this.dialogRef.close(true);
        },
        error: error => {
          this.apiError = apiErrorToMessage(error);
        }
      })
    }
  }

  private updateNameAndAccountingId(): Observable<Project | never> {
    if (!this.nameLock && this.addEditForm.value.accountingId != "" && this.addEditForm.value.name != "") {
      const request = {
        accountingId: this.addEditForm.value.accountingId ? this.prefix() + this.addEditForm.value.accountingId : this.data.project?.accountingId || "",
        name: this.addEditForm.value.name || "",
        parentId: undefined,
      };
      return (this.data.project?.id ?
          this.projectService.updateName(this.data.project?.id, request)
          : this.projectService.create(request)
      );
    }
    return EMPTY;
  }

  private updateDetails(): Observable<Project | never> {
    if (this.data.project?.id) {
      const formDetails = this.convertFormToProjectDetails();
      if (!this.compareDetails(formDetails, this.data.project?.details)) {
        return this.projectService.updateDetails(this.data.project?.id, formDetails);
      }
    }
    return EMPTY;
  }

  private updateStatus(): Observable<Project | never> {
    if (this.data.project?.id && this.addEditForm.controls.status.dirty) {

      return this.projectService.updateStatus(this.data.project?.id, {
        status: this.addEditForm.value.status || ProjectStatus.HOLD
      });
    }
    return EMPTY;
  }

  private compareDetails(a: ProjectDetails, b: ProjectDetails | undefined) {
    return Object.entries(a)
      .map(([key, value]) =>
        key == "location" || (b as any)[key] == value
      )
      .filter(value => !value)
      .length == 0
  }

  private convertFormToProjectDetails(): ProjectDetails {
    return {
      deadline: this.addEditForm.controls.deadline.value || undefined,
      clientPreferredLanguage: this.addEditForm.controls.clientPreferredLanguage.value || undefined,
      description: this.addEditForm.controls.description.value?.replaceAll("\n", "\n<br />") || undefined,
      location: {
        address: this.addEditForm.controls.locationAddress.value || undefined,
        mapsUrl: this.addEditForm.controls.locationMapsUrl.value || undefined,
      },
      dropboxProjectLink: this.addEditForm.controls.dropboxProjectLink.value || undefined,
      projectCompletedDate: this.addEditForm.controls.projectCompletedDate.value?.valueOf() || undefined,
    };
  }
}

export interface ProjectAddEditDialogData {
  project: Project | undefined;
  type: ProjectType | undefined;
  nextAccountingId: string | undefined;
  previousAccountingId: string | undefined;
}
